import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Settings, Upload, Image as ImageIcon, Save, Check, 
  Database, Download, Mail, Key, Shield, AlertCircle, FileUp,
  MessageCircle, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Configuracoes = () => {
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Backup states
  const [emailBackup, setEmailBackup] = useState('');
  const [backupFile, setBackupFile] = useState(null);
  const [generatingBackup, setGeneratingBackup] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [restoring, setRestoring] = useState(false);
  
  // Credenciais states
  const [showCredentials, setShowCredentials] = useState(false);
  const [currentEmail, setCurrentEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingCredentials, setChangingCredentials] = useState(false);
  
  // WhatsApp state
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [savingWhatsApp, setSavingWhatsApp] = useState(false);

  // Gmail SMTP state
  const [smtpGmail, setSmtpGmail] = useState('');
  const [smtpAppPassword, setSmtpAppPassword] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('Distribuidora Santa Ana');
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpMasked, setSmtpMasked] = useState('');
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchBackupEmail();
    fetchSiteSettings();
    fetchSmtpSettings();
  }, []);

  const fetchSmtpSettings = async () => {
    try {
      const resp = await axios.get(`${API}/email-settings`);
      setSmtpGmail(resp.data?.gmail || '');
      setSmtpFromName(resp.data?.from_name || 'Distribuidora Santa Ana');
      setSmtpConfigured(!!resp.data?.app_password_set);
      setSmtpMasked(resp.data?.app_password_masked || '');
    } catch (e) {
      console.error('Erro ao buscar SMTP:', e);
    }
  };

  const handleSaveSmtp = async () => {
    if (!smtpGmail || !smtpAppPassword) {
      toast.error('Preencha Gmail e App Password');
      return;
    }
    setSavingSmtp(true);
    try {
      await axios.post(`${API}/email-settings`, {
        gmail: smtpGmail,
        app_password: smtpAppPassword,
        from_name: smtpFromName
      });
      toast.success('Email configurado e testado com sucesso!');
      setSmtpAppPassword('');
      fetchSmtpSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const resp = await axios.post(`${API}/email-settings/test`, {});
      toast.success(resp.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao testar');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleRemoveSmtp = async () => {
    if (!window.confirm('Remover a configuração de email?')) return;
    try {
      await axios.delete(`${API}/email-settings`);
      toast.success('Configuração removida');
      setSmtpGmail('');
      setSmtpAppPassword('');
      setSmtpConfigured(false);
      setSmtpMasked('');
    } catch {
      toast.error('Erro ao remover');
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/configuracoes`);
      if (response.data.logo_base64) {
        setLogoPreview(response.data.logo_base64);
      }
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setWhatsappNumber(response.data?.whatsapp_number || '');
    } catch (error) {
      console.error('Erro ao buscar configurações do site:', error);
    }
  };

  const handleSaveWhatsApp = async () => {
    setSavingWhatsApp(true);
    try {
      await axios.put(`${API}/site-settings`, {
        whatsapp_number: whatsappNumber
      });
      toast.success('Número do WhatsApp salvo com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar número do WhatsApp');
      console.error(error);
    } finally {
      setSavingWhatsApp(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione uma imagem');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('Imagem muito grande. Máximo 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!logo) {
      toast.error('Selecione uma logo primeiro');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/configuracoes/logo`, {
        logo_base64: logo
      });
      toast.success('Logo salva com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar logo');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Backup functions
  const fetchBackupEmail = async () => {
    try {
      const response = await axios.get(`${API}/configuracoes/backup-email`);
      setEmailBackup(response.data.email_backup || '');
    } catch (error) {
      console.error('Erro ao buscar email de backup:', error);
    }
  };

  const saveBackupEmail = async () => {
    if (!emailBackup || !emailBackup.includes('@')) {
      toast.error('Email inválido');
      return;
    }

    try {
      await axios.post(`${API}/configuracoes/backup-email`, {
        email_backup: emailBackup
      });
      toast.success('Email salvo com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar email');
      console.error(error);
    }
  };

  const handleGenerateBackup = async () => {
    setGeneratingBackup(true);
    try {
      const response = await axios.get(`${API}/backup/generate`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_sanagest_${new Date().getTime()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Backup gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar backup');
      console.error(error);
    } finally {
      setGeneratingBackup(false);
    }
  };

  const handleSendBackupEmail = async () => {
    if (!emailBackup || !emailBackup.includes('@')) {
      toast.error('Configure um email válido primeiro');
      return;
    }

    // Mostrar aviso de que a funcionalidade de email não está configurada
    toast.info('Funcionalidade de envio por email não disponível. Use "Baixar Backup" para salvar seus dados.');
    
    // Fazer download automático do backup como alternativa
    handleGenerateBackup();
  };

  const handleRestoreBackup = async () => {
    if (!backupFile) {
      toast.error('Selecione um arquivo de backup');
      return;
    }

    if (!window.confirm('⚠️ ATENÇÃO: Restaurar o backup irá SUBSTITUIR todos os dados atuais. Deseja continuar?')) {
      return;
    }

    setRestoring(true);
    const formData = new FormData();
    formData.append('file', backupFile);

    try {
      const response = await axios.post(`${API}/backup/restore`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Backup restaurado! ${response.data.restored} coleções restauradas.`);
      setBackupFile(null);
      
      // Recarregar página após 2 segundos
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      toast.error('Erro ao restaurar backup');
      console.error(error);
    } finally {
      setRestoring(false);
    }
  };

  // Credentials functions
  const handleChangeCredentials = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (!currentEmail || !currentPassword) {
      toast.error('Informe o email e senha atuais');
      return;
    }

    setChangingCredentials(true);
    try {
      await axios.post(`${API}/auth/change-credentials`, {
        current_email: currentEmail,
        current_password: currentPassword,
        new_email: newEmail || currentEmail,
        new_password: newPassword || currentPassword
      });
      
      toast.success('Credenciais alteradas com sucesso! Faça login novamente.');
      
      // Limpar campos
      setCurrentEmail('');
      setCurrentPassword('');
      setNewEmail('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCredentials(false);
      
      // Redirecionar para login após 2 segundos
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Email ou senha atual incorretos');
      } else {
        toast.error('Erro ao alterar credenciais');
      }
      console.error(error);
    } finally {
      setChangingCredentials(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-lg">
          <Settings className="w-8 h-8 text-white" />
        </div>
        <div>
          <h2 className="text-3xl font-heading font-bold text-foreground">Configurações</h2>
          <p className="text-muted-foreground mt-1">Personalize seu sistema</p>
        </div>
      </div>

      {/* Upload de Logo */}
      <div className="stat-card">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Logo da Empresa</h3>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Esta logo será usada nos relatórios PDF (Romaneios e Fechamento Mensal)
        </p>

        <div className="space-y-4">
          {/* Preview da Logo */}
          {logoPreview && (
            <div className="flex items-center justify-center p-6 bg-muted/50 rounded-lg border-2 border-dashed border-border">
              <img 
                src={logoPreview} 
                alt="Logo Preview" 
                className="max-h-40 object-contain"
              />
            </div>
          )}

          {/* Upload */}
          <div className="flex items-center gap-3">
            <label className="btn-primary cursor-pointer flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Escolher Logo
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>

            {logo && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-secondary flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvar Logo
                  </>
                )}
              </button>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Formatos aceitos: JPG, PNG, SVG. Tamanho máximo: 5MB
          </p>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">
              Onde a logo será usada:
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
              <li>• PDF de Romaneios (Notas de Entrega)</li>
              <li>• PDF de Fechamento Mensal (Relatório de Gestão)</li>
              <li>• Futuros relatórios e documentos</li>
            </ul>
          </div>
        </div>
      </div>

      {/* WhatsApp do Catálogo */}
      <div className="stat-card">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-5 h-5 text-green-500" />
          <h3 className="font-heading font-semibold text-lg">WhatsApp do Catálogo</h3>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Configure o número de WhatsApp que aparecerá no botão flutuante da página de catálogo público.
          Os clientes poderão clicar para iniciar uma conversa diretamente.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Número do WhatsApp</label>
            <div className="flex gap-2">
              <div className="flex items-center bg-muted px-3 rounded-l-lg border border-r-0 border-border">
                <span className="text-sm text-muted-foreground">+55</span>
              </div>
              <input
                type="tel"
                value={whatsappNumber}
                onChange={(e) => {
                  // Permitir apenas números
                  const value = e.target.value.replace(/\D/g, '');
                  setWhatsappNumber(value);
                }}
                placeholder="11999998888"
                className="input-field flex-1 rounded-l-none"
                maxLength={11}
                data-testid="whatsapp-number-input"
              />
              <button
                onClick={handleSaveWhatsApp}
                disabled={savingWhatsApp}
                className="btn-primary flex items-center gap-2"
              >
                {savingWhatsApp ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Salvar
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Digite apenas os números (DDD + número). Ex: 11999998888
            </p>
          </div>

          {whatsappNumber && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-900 dark:text-green-200">
                    Link configurado:
                  </p>
                  <a 
                    href={`https://wa.me/55${whatsappNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-700 dark:text-green-300 hover:underline"
                  >
                    wa.me/55{whatsappNumber}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gmail SMTP - Envio de emails */}
      <div className="stat-card" data-testid="smtp-settings-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-red-500" />
            <h3 className="font-heading font-semibold text-lg">Envio de Email (Gmail)</h3>
          </div>
          {smtpConfigured && (
            <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" /> Conectado
            </span>
          )}
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
          <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
            <strong>Como obter a "Senha de App" do Google:</strong><br />
            1. Ative a <strong>Verificação em 2 etapas</strong> em sua conta Google.<br />
            2. Acesse{' '}
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline font-medium">
              myaccount.google.com/apppasswords
            </a>
            <br />
            3. Crie uma senha para "Mail" e cole os 16 caracteres abaixo.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Seu Gmail</label>
            <input
              type="email"
              value={smtpGmail}
              onChange={(e) => setSmtpGmail(e.target.value)}
              placeholder="seu.email@gmail.com"
              className="input-field w-full"
              data-testid="smtp-gmail-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Senha de App (16 caracteres)
              {smtpConfigured && !smtpAppPassword && (
                <span className="ml-2 text-xs text-muted-foreground font-mono">({smtpMasked})</span>
              )}
            </label>
            <input
              type="password"
              value={smtpAppPassword}
              onChange={(e) => setSmtpAppPassword(e.target.value)}
              placeholder={smtpConfigured ? '•••• •••• •••• ••••  (deixe vazio para manter)' : 'abcd efgh ijkl mnop'}
              className="input-field w-full font-mono"
              data-testid="smtp-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Nome do Remetente</label>
            <input
              type="text"
              value={smtpFromName}
              onChange={(e) => setSmtpFromName(e.target.value)}
              placeholder="Distribuidora Santa Ana"
              className="input-field w-full"
              data-testid="smtp-fromname-input"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={handleSaveSmtp}
              disabled={savingSmtp || !smtpAppPassword}
              className="btn-primary flex items-center gap-2"
              data-testid="smtp-save-btn"
            >
              {savingSmtp ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {smtpConfigured ? 'Atualizar' : 'Salvar e Testar'}
            </button>

            {smtpConfigured && (
              <>
                <button
                  onClick={handleTestSmtp}
                  disabled={testingSmtp}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="smtp-test-btn"
                >
                  <Mail className="w-4 h-4" />
                  {testingSmtp ? 'Enviando...' : 'Enviar Teste'}
                </button>
                <button
                  onClick={handleRemoveSmtp}
                  className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid="smtp-remove-btn"
                >
                  Remover
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sistema de Backup */}
      <div className="stat-card">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Sistema de Backup</h3>
        </div>

        <div className="space-y-4">
          {/* Email para backup */}
          <div>
            <label className="block text-sm font-medium mb-2">Email para Receber Backups</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={emailBackup}
                onChange={(e) => setEmailBackup(e.target.value)}
                placeholder="email@exemplo.com"
                className="input-field flex-1"
              />
              <button onClick={saveBackupEmail} className="btn-secondary">
                <Save className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={handleGenerateBackup}
              disabled={generatingBackup}
              className="btn-primary flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              {generatingBackup ? 'Gerando...' : 'Baixar Backup'}
            </button>

            <button
              onClick={handleSendBackupEmail}
              disabled={sendingEmail || !emailBackup}
              className="btn-secondary flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              {sendingEmail ? 'Enviando...' : 'Enviar por Email'}
            </button>

            <label className="btn-secondary flex items-center justify-center gap-2 cursor-pointer">
              <FileUp className="w-4 h-4" />
              Carregar Backup
              <input
                type="file"
                accept=".json"
                onChange={(e) => setBackupFile(e.target.files[0])}
                className="hidden"
              />
            </label>
          </div>

          {/* Arquivo selecionado */}
          {backupFile && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-900 dark:text-orange-200">
                    Arquivo selecionado: {backupFile.name}
                  </p>
                  <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                    ⚠️ Restaurar irá substituir TODOS os dados atuais
                  </p>
                </div>
                <button
                  onClick={handleRestoreBackup}
                  disabled={restoring}
                  className="btn-primary"
                >
                  {restoring ? 'Restaurando...' : 'Confirmar Restauração'}
                </button>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800 dark:text-blue-300">
                <p className="font-semibold mb-1">Sobre o Backup:</p>
                <ul className="space-y-1">
                  <li>• Inclui: produtos, clientes, vendedores, transportadoras e romaneios</li>
                  <li>• Formato: arquivo JSON</li>
                  <li>• Recomendado: fazer backup regularmente</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gerenciamento de Credenciais */}
      <div className="stat-card">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Segurança e Acesso</h3>
        </div>

        {!showCredentials ? (
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Altere seu email e senha de acesso ao sistema
            </p>
            <button
              onClick={() => setShowCredentials(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Key className="w-4 h-4" />
              Alterar Credenciais
            </button>
          </div>
        ) : (
          <form onSubmit={handleChangeCredentials} className="space-y-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <div className="flex gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800 dark:text-yellow-300">
                  <strong>Atenção:</strong> Para alterar suas credenciais, você precisa informar o email e senha atuais para validação.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email Atual *</label>
                <input
                  type="email"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  placeholder="email@atual.com"
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Senha Atual *</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Novo Email (opcional)</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="novo@email.com"
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Nova Senha (opcional)</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field"
                />
              </div>

              {newPassword && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Confirmar Nova Senha *</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-field"
                    required
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={changingCredentials}
                className="btn-primary"
              >
                {changingCredentials ? 'Alterando...' : 'Salvar Alterações'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCredentials(false);
                  setCurrentEmail('');
                  setCurrentPassword('');
                  setNewEmail('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
